/**
 * "Write with AI" next to a text field (partnership intro / page intro,
 * campaign description):
 * an optional brief, then three options from Claude — "Use this" fills the
 * field (Doreen still saves the page as usual).
 */
import { useState } from 'react';
import axios from '../../utils/axiosInstance';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';

export interface Option { angle: string; text: string }

export default function AiTextSuggest({
  campaignId, field, current, onUse, fetchOptions, note,
}: {
  /** Partnership page texts: the campaign + field to write. */
  campaignId?: string;
  field?: 'intro' | 'page_intro';
  current: string;
  onUse: (text: string) => void;
  /** Any other field: how to get the options. */
  fetchOptions?: (brief: string, current: string) => Promise<Option[]>;
  /** What Claude bases the options on (one line under the brief). */
  note?: string;
}) {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState<Option[]>([]);

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      if (fetchOptions) {
        setOptions(await fetchOptions(brief, current));
      } else {
        const res = await axios.post(`api/partnerships/${campaignId}/suggest-text/`, { field, brief, current });
        setOptions(res.data.options ?? []);
      }
    } catch (err: unknown) {
      const d = (err as { response?: { data?: { detail?: string } } })?.response?.data;
      setError(d?.detail || 'Could not get suggestions — try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="link" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => setOpen(true)}>
        <Icon name="sparkle" size={12} /> Write with AI
      </button>
    );
  }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 12, marginTop: 8, background: 'var(--bg-subtle)', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="input"
          style={{ flex: '1 1 260px' }}
          placeholder="Anything to stress? (optional) — e.g. mention the brand logo carousel"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
        />
        <Button variant="primary" onClick={run} disabled={busy}>
          <Icon name="sparkle" size={12} /> {busy ? 'Writing…' : options.length ? 'Try again' : 'Suggest'}
        </Button>
        <button type="button" className="link" style={{ fontSize: 12 }} onClick={() => { setOpen(false); setOptions([]); }}>Close</button>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
        {note ?? "Claude uses the campaign's name, dates, description, deadline, minimum discount and the placements for sale — nothing invented."}
        {current.trim() ? ' Your current text is used as a starting point.' : ''}
      </div>
      {error && <div style={{ fontSize: 12, color: 'var(--warn)' }}>{error}</div>}
      {options.map((o, i) => (
        <div key={i} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '10px 12px', display: 'grid', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>{o.angle}</div>
          <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-line', color: 'var(--ink-2)' }}>{o.text}</div>
          <div><Button variant="ghost" onClick={() => { onUse(o.text); setOpen(false); setOptions([]); }}>Use this</Button></div>
        </div>
      ))}
    </div>
  );
}
